import { ICONS } from 'components/Icon';
import { produce } from 'immer';
import { arrayUnique } from 'lib/riskbase';
import { without } from 'lodash';
import * as actions from 'store/actions';
import { SIGN_OUT } from 'store/auth/actions';
import {
  DELAY_FAILED_ERRORS_UPLOAD_MS,
  SYNC_DELAY_CHANGES_MS,
} from 'store/sync/constants';
import { SyncSteps, SyncState } from 'store/sync/types';
import { createReducer } from 'typesafe-actions';

const getInitialState = (): SyncState => ({
  flagKeyShouldDownloadCleanAssetData: 1,
  flagValueShouldDownloadCleanAssetData: 0,
  flagKeyShouldDownloadCleanOrganisationData: 1,
  flagValueShouldDownloadCleanOrganisationData: 0,
  downloadCleanData: false,

  syncStatus: {
    step: SyncSteps.waiting,
    icon: null,
    text: '',
    count: 0,
  },
  syncErrors: {
    [SyncSteps.data]: '',
    [SyncSteps.fileDownload]: '',
    [SyncSteps.fileUpload]: '',
    [SyncSteps.thumbnailDownload]: '',
  },

  serverTimestamp: 0,
  asset_removal_queue: [],
  asset_timestamps: {},
  assetIdsToDownload: [],
  doManualSync: false,
  downloadedFiles: {},
  downloadedFilesQueue: {},
  downloadedThumbnails: {},
  downloadedThumbnailsQueue: {},
  files_upload_queue: [],
  lastBgDataSyncTime: undefined,
  nextBgDataSyncTime: 1,
  nextAppUpdateCheckTime: 0,
  organisation_timestamps: {},
  pauseBgNetwork: 0,
  skipBgDataSyncUntilTime: 0,
  skipBgErrorUploadUntilTime: 0,
  skipBgNetworkUntilTime: Date.now() + (__DEV__ ? 60000 : 5000),
  appUpdateAvailable: false,
});

export const syncReducer = createReducer(getInitialState())
  .handleAction(SIGN_OUT.success, () => getInitialState())

  .handleAction(
    actions.doSyncSetStatus,
    (state, { payload: { step: syncState, count } }) => ({
      ...state,
      syncStatus: {
        step: syncState,
        count,
        text:
          syncState === SyncSteps.data
            ? 'Syncing\nData'
            : syncState === SyncSteps.fileDownload ||
                syncState === SyncSteps.fileUpload
              ? 'Files'
              : syncState === SyncSteps.thumbnailDownload
                ? 'Thumbnails'
                : '',
        icon:
          syncState === SyncSteps.fileDownload ||
          syncState === SyncSteps.thumbnailDownload
            ? ICONS.DownSolid
            : syncState === SyncSteps.fileUpload
              ? ICONS.UpSolid
              : null,
      },
    }),
  )

  .handleAction(actions.SET_SYNC_STATE, (state, action) => ({
    ...state,
    ...action.payload,
  }))
  .handleAction(actions.PAUSE_BG_NETWORK, (state) => ({
    ...state,
    pauseBgNetwork: state.pauseBgNetwork + 1,
  }))
  .handleAction(actions.UNPAUSE_BG_NETWORK, (state) => ({
    ...state,
    pauseBgNetwork: Math.max(state.pauseBgNetwork - 1, 0),
  }))
  .handleAction(actions.DO_MANUAL_SYNC, (state, action) => ({
    ...state,
    nextBgDataSyncTime: 1,
    skipBgNetworkUntilTime: 0,
    skipBgDataSyncUntilTime: 0,
    doManualSync: true,
    assetIdsToDownload: action.payload.addAssetIds?.length
      ? arrayUnique([
          ...state.assetIdsToDownload,
          ...action.payload.addAssetIds,
        ])
      : state.assetIdsToDownload,
  }))
  .handleAction(actions.DO_AUTO_SYNC, (state) => ({
    ...state,
    nextBgDataSyncTime: 1,
    skipBgNetworkUntilTime: 0,
    skipBgDataSyncUntilTime: 0,
  }))
  .handleAction(actions.UPDATE_ASSET_LIST, (state, action) => {
    const assetIds = Object.keys(action.payload.assets);
    if (!assetIds.length) {
      return state;
    }
    return {
      ...state,
      asset_timestamps: assetIds.reduce(
        (acc, assetId) => {
          acc[assetId] = action.payload.timestamp;
          return acc;
        },
        { ...state.asset_timestamps },
      ),
    };
  })
  .handleAction(actions.UPDATE_ORGANISATION_LIST, (state, action) => {
    const organisationIds = Object.keys(action.payload.organisations);
    if (!organisationIds.length) {
      return state;
    }
    return {
      ...state,
      organisation_timestamps: organisationIds.reduce(
        (acc, organisationId) => {
          acc[organisationId] = action.payload.timestamp;
          return acc;
        },
        { ...state.organisation_timestamps },
      ),
    };
  })
  .handleAction(actions.doShowSyncModal, (state, action) => ({
    ...state,
    downloadCleanData: action.payload
      ? action.payload.downloadCleanData
      : false,
  }))

  // Sync Data
  .handleAction(actions.DATA_SYNC.request, (state) => ({
    ...state,
    doManualSync: false,
    syncErrors: {
      ...state.syncErrors,
      [SyncSteps.data]: '',
    },
  }))
  .handleAction(actions.DATA_SYNC.success, (state, action) => ({
    ...state,
    serverTimestamp: action.payload.timestamp,
    assetIdsToDownload: [],
    appUpdateAvailable: action.payload.appUpdateAvailable,
    downloadCleanData: false,
  }))
  .handleAction(actions.DATA_SYNC.failure, (state, action) => ({
    ...state,
    assetIdsToDownload: [],
    syncErrors: {
      ...state.syncErrors,
      [SyncSteps.data]: action.payload.message,
    },
  }))

  // Download Thumbnail
  .handleAction(actions.DOWNLOAD_THUMBNAILS.request, (state) => ({
    ...state,
    syncErrors: {
      ...state.syncErrors,
      [SyncSteps.thumbnailDownload]: '',
    },
  }))
  .handleAction(actions.DOWNLOAD_THUMBNAILS.success, (state, action) =>
    produce(state, (draft) => {
      const files = action.payload;
      files.forEach((file) => {
        draft.downloadedThumbnails[file.id] = {
          assetId: state.downloadedThumbnailsQueue[file.id]!,
          width: file.width,
          height: file.height,
        };
        delete draft.downloadedThumbnailsQueue[file.id];
      });
    }),
  )
  .handleAction(actions.DOWNLOAD_THUMBNAILS.failure, (state, action) => ({
    ...state,
    syncErrors: {
      ...state.syncErrors,
      [SyncSteps.thumbnailDownload]: action.payload.message,
    },
  }))

  // Download Files
  .handleAction(actions.DOWNLOAD_FILES.request, (state) => ({
    ...state,
    syncErrors: {
      ...state.syncErrors,
      [SyncSteps.fileDownload]: '',
    },
  }))
  .handleAction(actions.DOWNLOAD_FILES.success, (state, action) =>
    produce(state, (draft) => {
      const fileIds = action.payload;
      fileIds.forEach((fileId) => {
        draft.downloadedFiles[fileId] = state.downloadedFilesQueue[fileId]!;
        delete draft.downloadedFilesQueue[fileId];
      });
    }),
  )
  .handleAction(actions.DOWNLOAD_FILES.failure, (state, action) => ({
    ...state,
    syncErrors: {
      ...state.syncErrors,
      [SyncSteps.fileDownload]: action.payload.message,
    },
  }))

  // Upload Files
  .handleAction(actions.UPLOAD_FILE.request, (state) => ({
    ...state,
    syncErrors: {
      ...state.syncErrors,
      [SyncSteps.fileUpload]: '',
    },
  }))
  .handleAction(actions.UPLOAD_FILE.failure, (state, action) => ({
    ...state,
    syncErrors: {
      ...state.syncErrors,
      [SyncSteps.fileUpload]: action.payload.message,
    },
  }))

  .handleAction(actions.doSetAppUpdateAvailable, (state, action) => ({
    ...state,
    appUpdateAvailable: action.payload.status,
  }))

  .handleAction(actions.ENQUEUE_ASSET_DELETE, (state, action) => ({
    ...state,
    asset_removal_queue: [...state.asset_removal_queue, ...action.payload],
  }))

  .handleAction(actions.DELETE_ASSETS, (state, action) =>
    produce(state, (draft) => {
      const assetIds = action.payload.ids;
      draft.downloadedFiles = Object.fromEntries(
        Object.entries(state.downloadedFiles).filter(
          ([, tnAssetId]) => !assetIds.includes(tnAssetId),
        ),
      );
      draft.downloadedFilesQueue = Object.fromEntries(
        Object.entries(state.downloadedFilesQueue).filter(
          ([, tnAssetId]) => !assetIds.includes(tnAssetId),
        ),
      );
      draft.downloadedThumbnails = Object.fromEntries(
        Object.entries(state.downloadedThumbnails).filter(
          ([, tnAssetId]) =>
            !assetIds.includes(
              typeof tnAssetId === 'string' ? tnAssetId : tnAssetId.assetId,
            ),
        ),
      );
      draft.downloadedThumbnailsQueue = Object.fromEntries(
        Object.entries(state.downloadedThumbnailsQueue).filter(
          ([, tnAssetId]) => !assetIds.includes(tnAssetId),
        ),
      );
      draft.asset_removal_queue = state.asset_removal_queue.filter(
        (assetId) => !assetIds.includes(assetId),
      );
      draft.asset_timestamps = Object.fromEntries(
        Object.entries(state.asset_timestamps).filter(
          ([assetId]) => !assetIds.includes(assetId),
        ),
      );
    }),
  )

  .handleAction(actions.DELETE_ORGANISATIONS, (state, action) =>
    produce(state, (draft) => {
      const organisationIds = action.payload.ids;
      draft.organisation_timestamps = Object.fromEntries(
        Object.entries(state.organisation_timestamps).filter(
          ([organisationId]) => !organisationIds.includes(organisationId),
        ),
      );
    }),
  )

  .handleAction(actions.SET_ASSET_DELTA, (state) => ({
    ...state,
    nextBgDataSyncTime: 0,
    skipBgDataSyncUntilTime: 0,
    skipBgNetworkUntilTime: Date.now() + SYNC_DELAY_CHANGES_MS,
  }))

  .handleAction(actions.MANAGE_ASSET_FILE_DOWNLOADS, (state, action) =>
    produce(state, (draft) => {
      Object.values(action.payload).forEach((asset) => {
        // Queue download of files
        asset.downloadFiles?.forEach((fileId) => {
          if (!draft.downloadedFiles[fileId]) {
            draft.downloadedFilesQueue[fileId] = asset.id;
          }
        });
        // Queue download of files
        asset.downloadThumbnails?.forEach((fileId) => {
          if (!draft.downloadedThumbnails[fileId]) {
            draft.downloadedThumbnailsQueue[fileId] = asset.id;
          }
        });
      });
    }),
  )

  .handleAction(actions.REMOVE_DOWNLOADED_THUMBNAILS, (state, action) =>
    produce(state, (draft) => {
      const { fileIds } = action.payload;
      fileIds.forEach((fileId) => {
        delete draft.downloadedThumbnails[fileId];
      });
      fileIds.forEach((fileId) => {
        delete draft.downloadedThumbnailsQueue[fileId];
      });
    }),
  )

  .handleAction(actions.REMOVE_DOWNLOADED_FILES, (state, action) =>
    produce(state, (draft) => {
      const { fileIds } = action.payload;
      fileIds.forEach((fileId) => {
        delete draft.downloadedFiles[fileId];
      });
      fileIds.forEach((fileId) => {
        delete draft.downloadedFilesQueue[fileId];
      });
    }),
  )

  .handleAction(actions.ADD_DOWNLOADED_THUMBNAIL, (state, action) => ({
    ...state,
    downloadedThumbnails: {
      ...state.downloadedThumbnails,
      [action.payload.fileId]: action.payload.assetId,
    },
  }))

  .handleAction(actions.ADD_FILE_TO_UPLOAD_QUEUE, (state, action) => ({
    ...state,
    files_upload_queue: [...state.files_upload_queue, ...action.payload],
  }))

  .handleAction(actions.UPLOAD_FILE.success, (state, action) => ({
    ...state,
    files_upload_queue: state.files_upload_queue.filter(
      (fileId) => fileId !== action.payload,
    ),
  }))
  .handleAction(actions.doSetFileUploadFailed, (state, action) =>
    produce(state, (draft) => {
      // Move failed file to the end of the queue, let other files have a chance
      const { fileId } = action.payload;
      draft.files_upload_queue = without(draft.files_upload_queue, fileId);
      draft.files_upload_queue.push(fileId);
    }),
  )

  .handleAction(actions.UPLOAD_ERRORS.failure, (state) => ({
    ...state,
    skipBgErrorUploadUntilTime: Date.now() + DELAY_FAILED_ERRORS_UPLOAD_MS,
  }))

  .handleAction(actions.UPGRADE, (state) =>
    produce(state, (draft) => {
      if (
        state.flagValueShouldDownloadCleanAssetData !==
        state.flagKeyShouldDownloadCleanAssetData
      ) {
        draft.asset_timestamps = Object.fromEntries(
          Object.keys(draft.asset_timestamps).map((assetId) => [assetId, 1]),
        );
        draft.flagValueShouldDownloadCleanAssetData =
          draft.flagKeyShouldDownloadCleanAssetData;
      }
      if (
        state.flagValueShouldDownloadCleanOrganisationData !==
        state.flagKeyShouldDownloadCleanOrganisationData
      ) {
        draft.organisation_timestamps = Object.fromEntries(
          Object.keys(draft.organisation_timestamps).map((organisationId) => [
            organisationId,
            1,
          ]),
        );
        draft.flagValueShouldDownloadCleanOrganisationData =
          draft.flagKeyShouldDownloadCleanOrganisationData;
      }
    }),
  );
